import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { Badge } from 'react-bootstrap';
import { Prod01 } from '../../constants/ActionType';
import DateRangePicker, { DateRangeSummary } from '../common/DateRangePicker';
import { defaultRange } from '../../utils/dateRange';
import { useSortableTable, SortableTh, nameFromString } from '../SortableTable';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateFormat';

// Proof of device service + non-transmitting device worklist. Data and scope come from GET /telemetry-report
// (tenant-scoped; regular nurses see only their patients). Exports are audited server-side.
export const STATUS_VARIANT = {
    Transmitting: 'success',
    'No data 3+ days': 'warning',
    'No data 7+ days': 'danger',
    'Never transmitted': 'secondary',
};
const SERVER_SORT_KEYS = ['patient', 'emrId', 'practice', 'provider', 'devices', 'readings', 'daysWithReadings', 'lastReading', 'daysSince', 'status'];

export const TELEMETRY_SORT_COLUMNS = {
    patient: { type: 'name', value: (r) => nameFromString(r.patientName) },
    emrId: { type: 'string', value: (r) => r.emrId },
    practice: { type: 'string', value: (r) => r.practice },
    provider: { type: 'name', value: (r) => nameFromString(r.provider) },
    devices: { type: 'string', value: (r) => r.deviceTypes },
    readings: { type: 'number', value: (r) => r.readings },
    daysWithReadings: { type: 'number', value: (r) => r.daysWithReadingsMonth },
    lastReading: { type: 'date', value: (r) => r.lastReadingAt },
    daysSince: { type: 'number', value: (r) => r.daysSinceLastReading },
    status: { type: 'number', value: (r) => r.statusRank },
};
const READING_SORT_COLUMNS = {
    at: { type: 'date', value: (r) => r.at },
    device: { type: 'string', value: (r) => r.deviceType },
    systolic: { type: 'number', value: (r) => r.systolic },
    diastolic: { type: 'number', value: (r) => r.diastolic },
    pulse: { type: 'number', value: (r) => r.pulse },
    weight: { type: 'number', value: (r) => r.weightLbs },
    glucose: { type: 'number', value: (r) => r.glucose },
    spo2: { type: 'number', value: (r) => r.spo2 },
};

const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};
const filenameFrom = (res, fallback) => {
    const cd = res.headers && res.headers['content-disposition'];
    const m = cd && cd.match(/filename="?([^"]+)"?/);
    return m ? m[1] : fallback;
};
const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const errText = (e) => (e && e.response && e.response.data && e.response.data.message) || 'Request failed';

export const StatusBadge = ({ status }) => (
    <Badge pill bg={STATUS_VARIANT[status] || 'secondary'} className="text-white" style={{ fontSize: 12, fontWeight: 400 }}>{status}</Badge>
);

const Readings = ({ readings, idPrefix }) => {
    const sort = useSortableTable(readings, { id: `${idPrefix}.telemetryReadings`, columns: READING_SORT_COLUMNS, defaultSort: { key: 'at', dir: 'asc' } });
    if (!readings.length) return <p className="text-muted m-2"><small>No readings received in this date range.</small></p>;
    return (
        <table className="table table-sm table-bordered mb-0" data-testid="telemetry-readings">
            <thead className="thead-light">
                <tr>
                    <SortableTh sortKey="at" sort={sort}>Date / time (ET)</SortableTh>
                    <SortableTh sortKey="device" sort={sort}>Device</SortableTh>
                    <SortableTh sortKey="systolic" sort={sort}>Systolic</SortableTh>
                    <SortableTh sortKey="diastolic" sort={sort}>Diastolic</SortableTh>
                    <SortableTh sortKey="pulse" sort={sort}>Pulse</SortableTh>
                    <SortableTh sortKey="weight" sort={sort}>Weight (lb)</SortableTh>
                    <SortableTh sortKey="glucose" sort={sort}>Glucose</SortableTh>
                    <SortableTh sortKey="spo2" sort={sort}>SpO2 %</SortableTh>
                    <th>Flags</th>
                </tr>
            </thead>
            <tbody>
                {sort.rows.map((r) => (
                    <tr key={r.id}>
                        <td>{r.at ? formatDateTime(r.at) : `${formatDate(r.date)} ${formatTime(r.time)}`}</td>
                        <td>{r.deviceType}{r.imei ? <small className="text-muted"> {r.imei}</small> : null}</td>
                        <td>{dash(r.systolic)}</td>
                        <td>{dash(r.diastolic)}</td>
                        <td>{dash(r.pulse)}</td>
                        <td>{dash(r.weightLbs)}</td>
                        <td>{dash(r.glucose)}</td>
                        <td>{dash(r.spo2)}</td>
                        <td>{r.flags.length ? <span style={{ color: '#B71C1C', fontWeight: 600 }}>{r.flags.join(', ')}</span> : ''}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

/**
 * Shared Telemetry Report body for the Admin (/report/telemetary) and Nurse (/Nurse/TelemetryDataReport) screens.
 * idPrefix keys the remembered table sort ('admin' | 'nurse').
 */
const TelemetryReportView = ({ idPrefix = 'admin', title = 'Telemetry Report' }) => {
    const [range, setRange] = useState(() => defaultRange());
    const [patientOptions, setPatientOptions] = useState([]);
    const [patient, setPatient] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [deviceType, setDeviceType] = useState('');
    const [doctorId, setDoctorId] = useState('');
    const [practice, setPractice] = useState('');
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [open, setOpen] = useState({});
    const [busy, setBusy] = useState('');

    const params = useMemo(() => {
        const p = { startDate: range.startDate, endDate: range.endDate };
        if (patient) p.patientId = patient.value;
        if (search) p.search = search;
        if (status) p.status = status;
        if (deviceType) p.deviceType = deviceType;
        if (doctorId) p.doctorId = doctorId;
        if (practice) p.practice = practice;
        return p;
    }, [range, patient, search, status, deviceType, doctorId, practice]);

    useEffect(() => {
        axios.get(`${Prod01}/telemetry-report/patients`)
            .then(({ data }) => setPatientOptions((data || []).map((p) => ({ value: p.id, label: `${p.name}${p.emrId ? ` (${p.emrId})` : ''}` }))))
            .catch(() => setPatientOptions([]));
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        setOpen({});
        axios.get(`${Prod01}/telemetry-report`, { params })
            .then(({ data }) => setReport(data))
            .catch((e) => { setReport(null); setError(errText(e)); })
            .finally(() => setLoading(false));
    }, [params]);

    useEffect(() => { load(); }, [load]);

    const rows = useMemo(() => (report ? report.rows : []), [report]);
    const tableSort = useSortableTable(rows, { id: `${idPrefix}.telemetryReport`, columns: TELEMETRY_SORT_COLUMNS, defaultSort: { key: 'patient', dir: 'asc' } });

    const toggle = (row) => {
        const id = row.patientId;
        if (open[id]) { setOpen((o) => ({ ...o, [id]: undefined })); return; }
        setOpen((o) => ({ ...o, [id]: { loading: true, readings: [] } }));
        axios.get(`${Prod01}/telemetry-report/patient/${id}/readings`, { params: { startDate: range.startDate, endDate: range.endDate } })
            .then(({ data }) => setOpen((o) => ({ ...o, [id]: { loading: false, readings: data.readings || [] } })))
            .catch((e) => setOpen((o) => ({ ...o, [id]: { loading: false, readings: [], error: errText(e) } })));
    };

    const exportFile = async (format, patientId) => {
        const key = `${format}:${patientId || 'all'}`;
        setBusy(key);
        try {
            const sortParams = tableSort.sortKey && SERVER_SORT_KEYS.includes(tableSort.sortKey) ? { sortBy: tableSort.sortKey, sortDir: tableSort.sortDir } : {};
            const p = patientId ? { startDate: range.startDate, endDate: range.endDate, patientId } : { ...params, ...sortParams };
            const res = await axios.get(`${Prod01}/telemetry-report/export.${format}`, { params: p, responseType: 'blob' });
            saveBlob(res.data, filenameFrom(res, `telemetry-report.${format}`));
        } catch (e) {
            setError(`Export failed: ${errText(e)}`);
        } finally {
            setBusy('');
        }
    };

    const filters = (report && report.filters) || { practices: [], providers: [], deviceTypes: [] };
    const totals = report && report.totals;

    return (
        <div className="telemetry-report" data-testid="telemetry-report">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
                <h5 className="card-title mt-2">{title}</h5>
                <div>
                    <button type="button" className="btn btn-sm btn-outline-primary mr-2" disabled={!!busy || !rows.length} onClick={() => exportFile('csv')}>
                        {busy === 'csv:all' ? 'Exporting…' : 'Export CSV'}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={!!busy || !rows.length} onClick={() => exportFile('pdf')}>
                        {busy === 'pdf:all' ? 'Exporting…' : 'Export PDF'}
                    </button>
                </div>
            </div>
            <p className="text-muted mb-2"><small>Proof of device service and devices that stopped transmitting. Days with readings are counted in the calendar month of the range end ({report ? report.monthLabel : 'this month'}); {report ? report.thresholdDays : 16} days meets the threshold, 2–15 days is the lower band.</small></p>

            <div className="row align-items-end">
                <div className="col-md-4 mb-2">
                    <label className="form-label mb-0" htmlFor="telemetry-patient"><small>Patient (A–Z)</small></label>
                    <Select inputId="telemetry-patient" isClearable placeholder="All patients" options={patientOptions} value={patient} onChange={setPatient} />
                </div>
                <div className="col-md-4 mb-2">
                    <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); }} className="d-flex">
                        <div className="flex-grow-1">
                            <label className="form-label mb-0" htmlFor="telemetry-search"><small>Search name or EHR ID</small></label>
                            <input id="telemetry-search" className="form-control" placeholder="Type and press Enter" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
                        </div>
                        <button type="submit" className="btn btn-sm btn-primary ml-1 align-self-end" style={{ height: 38 }}>Search</button>
                    </form>
                </div>
                <div className="col-md-4 mb-2">
                    <DateRangePicker id={`${idPrefix}-telemetry-range`} startDate={range.startDate} endDate={range.endDate} onChange={({ startDate, endDate }) => setRange({ startDate, endDate })} />
                </div>
            </div>
            <div className="row">
                <div className="col-md-3 mb-2">
                    <select aria-label="Status filter" className="form-control form-control-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">All statuses</option>
                        {Object.keys(STATUS_VARIANT).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div className="col-md-3 mb-2">
                    <select aria-label="Device type filter" className="form-control form-control-sm" value={deviceType} onChange={(e) => setDeviceType(e.target.value)}>
                        <option value="">All device types</option>
                        {['bp', 'weight', 'spO2', 'glucose'].concat(filters.deviceTypes.filter((d) => !['bp', 'weight', 'spO2', 'glucose'].includes(d))).map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                </div>
                <div className="col-md-3 mb-2">
                    <select aria-label="Provider filter" className="form-control form-control-sm" value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
                        <option value="">All providers</option>
                        {filters.providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                </div>
                <div className="col-md-3 mb-2">
                    <select aria-label="Practice filter" className="form-control form-control-sm" value={practice} onChange={(e) => setPractice(e.target.value)}>
                        <option value="">All practices</option>
                        {filters.practices.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
            </div>

            <DateRangeSummary startDate={range.startDate} endDate={range.endDate} />
            {totals ? (
                <p className="mb-2" data-testid="telemetry-totals">
                    <small>
                        <b>{totals.patients}</b> patients · <b>{totals.readings}</b> readings ·{' '}
                        {Object.keys(STATUS_VARIANT).map((s) => <span key={s} className="mr-2"><StatusBadge status={s} /> {totals.byStatus[s] || 0}</span>)}
                    </small>
                </p>
            ) : null}
            {error ? <div className="alert alert-danger py-1" role="alert">{error}</div> : null}
            {loading ? <p className="text-muted">Loading…</p> : (
                <div className="table-responsive">
                    <table className="styled-table" style={{ width: '100%' }} data-testid="telemetry-summary">
                        <thead>
                            <tr>
                                <th aria-label="Expand" />
                                <SortableTh sortKey="patient" sort={tableSort}>Patient</SortableTh>
                                <SortableTh sortKey="emrId" sort={tableSort}>EHR ID</SortableTh>
                                <SortableTh sortKey="practice" sort={tableSort}>Practice</SortableTh>
                                <SortableTh sortKey="provider" sort={tableSort}>Provider</SortableTh>
                                <SortableTh sortKey="devices" sort={tableSort}>Device(s) / IMEI</SortableTh>
                                <SortableTh sortKey="readings" sort={tableSort}>Readings</SortableTh>
                                <SortableTh sortKey="daysWithReadings" sort={tableSort} title="Days with readings in the calendar month (16 = threshold, 2–15 = band)">Days this month</SortableTh>
                                <SortableTh sortKey="lastReading" sort={tableSort}>Last reading (ET)</SortableTh>
                                <SortableTh sortKey="daysSince" sort={tableSort}>Days since</SortableTh>
                                <SortableTh sortKey="status" sort={tableSort}>Status</SortableTh>
                                <th>Export</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!tableSort.rows.length ? (
                                <tr><td colSpan={12} className="text-center text-muted">No patients match these filters.</td></tr>
                            ) : tableSort.rows.map((r) => (
                                <React.Fragment key={r.patientId}>
                                    <tr>
                                        <td>
                                            <button type="button" className="btn btn-sm btn-link p-0" aria-expanded={!!open[r.patientId]} aria-label={`Readings for ${r.patientName}`} onClick={() => toggle(r)}>
                                                {open[r.patientId] ? '▾' : '▸'}
                                            </button>
                                        </td>
                                        <td>{r.patientName}{r.active ? null : <small className="text-muted"> (Deactivated)</small>}</td>
                                        <td>{dash(r.emrId)}</td>
                                        <td>{dash(r.practice)}</td>
                                        <td>{r.provider}</td>
                                        <td>{r.devices.length ? r.devices.map((d) => <div key={d.id}><small>{d.deviceType} {d.imei || ''}</small></div>) : '—'}</td>
                                        <td>{r.readings}{r.flagged ? <small style={{ color: '#B71C1C' }}> ({r.flagged} flagged)</small> : null}</td>
                                        <td><b style={{ color: r.meetsThreshold ? 'green' : r.daysWithReadingsMonth >= 2 ? '#B26A00' : '#B71C1C' }}>{r.daysWithReadingsMonth}</b> / {report.thresholdDays}<div><small className="text-muted">{r.daysBand}</small></div></td>
                                        <td>{r.lastReadingEt || 'Never'}</td>
                                        <td>{dash(r.daysSinceLastReading)}</td>
                                        <td><StatusBadge status={r.status} /></td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <button type="button" className="btn btn-sm btn-link p-0 mr-2" disabled={!!busy} onClick={() => exportFile('csv', r.patientId)}>CSV</button>
                                            <button type="button" className="btn btn-sm btn-link p-0" disabled={!!busy} onClick={() => exportFile('pdf', r.patientId)}>PDF</button>
                                        </td>
                                    </tr>
                                    {open[r.patientId] ? (
                                        <tr>
                                            <td colSpan={12} style={{ background: '#F7F8FB' }}>
                                                {open[r.patientId].loading ? <small className="text-muted">Loading readings…</small>
                                                    : open[r.patientId].error ? <small className="text-danger">{open[r.patientId].error}</small>
                                                        : <Readings readings={open[r.patientId].readings} idPrefix={idPrefix} />}
                                            </td>
                                        </tr>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TelemetryReportView;
